/**
 * DL-01 decisions 1–3 — who sees "Download images". PURE; the SERVER is the
 * boundary (§A-3), this only decides what to render.
 *
 *   own tenant      role ∈ {tenant_admin, project_manager, expert_analyst}
 *                   (decision 1 — not pre_screener, not pilot, not back_office)
 *                   plus platform_admin — the superuser every lyse surface
 *                   admits, and the backend's own-tenant path (#574) does too;
 *                   DA, instruction AND project scope.
 *   customer        a `member` row with the `project_customer` grant, only
 *                   while the owner's `projects.customer_image_download` flag
 *                   is ON (decision 2); DA and instruction scope only — never
 *                   the project (story §B). The hot-tier rule (decision 3) is
 *                   the server's: a 403 `customer_download_not_hot` is worded
 *                   by the dialog, not predicted here.
 *
 * Roles are read as a transport array (`/me`); membership is a set test, not
 * a `role ===` literal (SB2-CC-01 AC9).
 */
import type { DownloadScopeKind } from "./manifestApi.js";

export const OWNER_DOWNLOAD_ROLES: ReadonlySet<string> = new Set([
  "tenant_admin",
  "project_manager",
  "expert_analyst",
  "platform_admin",
]);

export const CUSTOMER_DOWNLOAD_ROLE = "project_customer";

/** Does the caller hold an own-tenant download role at all? */
export function hasOwnerDownloadRole(
  roles: readonly string[] | undefined,
): boolean {
  return (roles ?? []).some((r) => OWNER_DOWNLOAD_ROLES.has(r));
}

/**
 * Should a table render its "Download" column? Only when at least one row
 * could carry the action: the caller holds an owner role, or the list has
 * cross-tenant `member` rows (a `project_customer` row decides per row via
 * the owner's flag). Otherwise the column is omitted — never an empty one
 * (a pilot, or a platform admin before this helper, saw a blank column).
 */
export function showDownloadColumn(
  roles: readonly string[] | undefined,
  hasMemberRows: boolean,
): boolean {
  return hasOwnerDownloadRole(roles) || hasMemberRows;
}

export interface DownloadEligibilityInput {
  /** The caller's `/me` roles (undefined while unknown → not eligible). */
  roles: readonly string[] | undefined;
  /** The row's cross-tenant tell; absent = the caller's own tenant. */
  ownership?: "owner" | "member" | undefined;
  /** The caller's access role on a `member` row (`project_customer`, …). */
  accessRole?: string | null | undefined;
  /** The owner's flag, read from the project (null = not read yet). */
  customerDownloadEnabled?: boolean | null | undefined;
  scopeKind: DownloadScopeKind;
}

/** Is this a customer row — the one case that needs the project's flag? */
export function needsCustomerFlag(
  i: Pick<DownloadEligibilityInput, "ownership" | "accessRole" | "scopeKind">,
): boolean {
  return (
    i.ownership === "member" &&
    i.accessRole === CUSTOMER_DOWNLOAD_ROLE &&
    i.scopeKind !== "project"
  );
}

export function canDownloadImages(i: DownloadEligibilityInput): boolean {
  if (i.ownership === "member") {
    return needsCustomerFlag(i) && i.customerDownloadEnabled === true;
  }
  return hasOwnerDownloadRole(i.roles);
}
